import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import {
  CouponIcon,
  DeleteIcon,
  GKButton,
  GKDataGrid,
  Grid2,
  IconButton,
  NoData,
  PlusIcon,
  PopConfirm,
  Show,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { FeePlanById } from "interfaces/Revenue";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteFeePlanMutation,
  useGetFeePlansQuery,
} from "store/features/Revenue/FeePlan";
import { moneyCommaSeparator } from "utils";
import { handleCapitalize } from "utils/common";
import { extraColumnsField } from "utils/helpers";

const FeePlan = () => {
  const navigate = useNavigate();

  const [confirmationModal, setConfirmationModal] =
    useState<HTMLElement | null>(null);
  const [selectFeePlanId, setSelectFeePlanId] = useState<any>(null);

  const requestBody = useGenerateQueryParams("feePlans");
  const { data, isFetching } = useGetFeePlansQuery(requestBody);

  const [deleteFeePlan, { isLoading }] = useDeleteFeePlanMutation();

  const handleDeleteFeePlan = (
    e: React.MouseEvent<HTMLElement>,
    params: GridRenderCellParams<FeePlanById>,
  ) => {
    e.stopPropagation();
    setSelectFeePlanId(params.id);
    setConfirmationModal(e.currentTarget);
  };

  const handleNavigate = (path: string) => navigate(path);

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `Fee Plan (${data?.totalCount || 0})`,
        "name",
        200,
        1,
      ),
      valueGetter: (params) => params.row.name || "-",
    },
    {
      ...extraColumnsField("Revenue Products", "revenueProducts", 150, 1),
      valueGetter: (params) =>
        params?.row?.revenueProducts
          ?.map((dataItem: string) =>
            handleCapitalize(dataItem?.replace("_", " ")),
          )
          .join(", ") || "-",
    },
    {
      ...extraColumnsField("AUM Mapped (₹)", "totalAumMapped", 150, 1),
      type: "number",
      valueGetter: (param) => param?.row?.totalAumMapped,
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },
    {
      ...extraColumnsField("Portfolio Mapped", "totalPortfolio", 150, 1),
      type: "number",
      valueGetter: (param) => param?.row?.totalPortfolio,
    },
    {
      ...extraColumnsField(
        "Total Fee Collected (₹)",
        "totalFeeCollected",
        200,
        1,
      ),
      type: "number",
      valueGetter: (param) => param?.row?.totalFeeCollected,
      valueFormatter: (params) => moneyCommaSeparator(params.value || 0),
    },
    {
      ...extraColumnsField(
        "Total Fee Generated (₹)",
        "totalFeeGenerated",
        200,
        1,
      ),
      type: "number",
      valueGetter: (param) => param?.row?.totalFeeGenerated,
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },
    {
      ...extraColumnsField("Actions", "action", 100, 1),
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Grid2 container gap={1} display={"flex"} justifyContent={"flex-end"}>
          <IconButton
            className="outline"
            color="error"
            disabled={params.row.id === selectFeePlanId && isLoading}
            onClick={(e) => handleDeleteFeePlan(e, params)}
          >
            <DeleteIcon width={20} />
          </IconButton>
        </Grid2>
      ),
    },
  ];

  return (
    <>
      <GKDataGrid
        loading={isFetching}
        columns={columns}
        rows={data?.data || []}
        maxHeight={"calc(100vh - 339px)"}
        name="feePlans"
        headerComponent={
          <GKButton
            variant="contained"
            onClick={() => handleNavigate("/app/revenue/fee-plans/")}
            startIcon={<PlusIcon width={16} />}
          >
            Create Fee Plan
          </GKButton>
        }
        onRowClick={(row) => {
          navigate(`/app/revenue/fee-plans/${row.id}/`);
        }}
        totalCount={data?.totalCount || 0}
        emptyComponent={
          <NoData
            text="Set Up Your First Fee Plan"
            subText="Create a plan to start managing your fees efficiently."
            customImg={
              <CouponIcon height={80} width={80} color="var(--neutral-100)" />
            }
            additionalComponent={
              <GKButton
                variant="contained"
                onClick={() => handleNavigate("/app/revenue/fee-plans/")}
                startIcon={<PlusIcon width={16} />}
              >
                Create Fee Plan
              </GKButton>
            }
          />
        }
      />

      <Show.When isTrue={Boolean(confirmationModal)}>
        <PopConfirm
          anchorEl={confirmationModal}
          open={Boolean(confirmationModal)}
          setOpen={setConfirmationModal}
          onSuccess={async () => {
            try {
              await deleteFeePlan(selectFeePlanId).unwrap();
            } catch (err) {
              console.error("Delete single automation failed:", err);
            }
          }}
        />
      </Show.When>
    </>
  );
};

export default FeePlan;
